import { HumanizeDateTime } from "@/lib/dates";
import { useLocation, useNavigate } from "react-router";
import { useOrgRoutes, useRoutes } from "@/routes";
import { ruleCategoryLabel } from "@/pages/security/policy-data";
import { getRuleTitleFallback } from "@/pages/security/risk-utils";
import {
  IdentityPanel,
  IdentityPanelEmpty,
  IdentityPanelRow,
} from "./IdentityPanel";
import { identityHandoffs } from "./identityHandoffs";
import {
  type FindingsFilter,
  identityFindingsHref,
  RISK_UNAVAILABLE,
} from "./identityFindingsLink";
import { EmployeeShadowAISection } from "@/components/observe/EmployeeShadowAISection";
import { useIdentityOutlet } from "./identityRoute";
import { useRBAC } from "@/hooks/useRBAC";
import { RankedBarList } from "@/components/chart/RankedBarList";
import { ShareBar } from "@/components/chart/ShareBar";
import { IdentitySection } from "./IdentitySection";
import { sectionMeta } from "./sectionMeta";
import { shadowMCPAccessSummaryOf } from "@/components/shadow-mcp/shadowMCPInventoryStatus";
import {
  retryFailed,
  riskMatchedOnLabel,
  useCanReadRisk,
  useIdentityChallenges,
  useIdentityPrincipalUrn,
  useIdentityProject,
  useIdentityRisk,
  useIdentityShadowServers,
  useIdentityWindow,
} from "./useIdentityQueries";

const TOP_RULES = 5;
const DENIED_ROWS = 8;

export default function IdentitySecurity(): JSX.Element {
  const canReadRisk = useCanReadRisk();
  // Employee detections are a project read on the server (the email pins
  // the request to one person), so this panel gates on that scope rather
  // than on the org:admin the risk panels need.
  // Checked against the project the request goes to: a read grant on some
  // other project would enable the panel and then fail the request.
  const { hasAnyScopeInProject, isLoading: grantsLoading } = useRBAC();
  const project = useIdentityProject();
  const canReadDetections = hasAnyScopeInProject(
    ["project:read", "project:write"],
    project.id,
  );
  const { identity, urn } = useIdentityOutlet();
  // Only an enrolled person has device scans behind them; an API key or an
  // external identity has nothing to show.
  const isEmployee = identity.kind === "user" && identity.userIds.length > 0;
  const employeeEmail = isEmployee ? (identity.emails[0] ?? null) : null;
  const { from, to } = useIdentityWindow();
  const location = useLocation();
  const routes = useRoutes();
  const orgRoutes = useOrgRoutes();
  // The same principal the challenge query uses, so the link filters to the
  // principal the panel counted rather than opening the whole log.
  const principalUrn = useIdentityPrincipalUrn(identity);
  const handoffs = identityHandoffs(
    identity,
    routes,
    orgRoutes,
    principalUrn,
    new URLSearchParams(location.search),
  );

  const riskQuery = useIdentityRisk(identity, from, to);
  const navigate = useNavigate();
  const findingsHref = (filter: FindingsFilter) =>
    identityFindingsHref(routes, urn, location.search, filter);
  const showFindings = (filter: FindingsFilter) =>
    void navigate(findingsHref(filter));
  // Asked for as a denied-only slice so the count below is the API's total for
  // exactly what the panel claims, rather than however many denials happened
  // to fall inside one capped page of mixed outcomes.
  const deniedQuery = useIdentityChallenges(identity, from, to, {
    outcome: "deny",
  });
  const unresolvedQuery = useIdentityChallenges(identity, from, to, {
    outcome: "deny",
    resolved: false,
    limit: 1,
  });
  const shadowQuery = useIdentityShadowServers(identity, from, to);

  const categories = riskQuery.data?.categories ?? [];
  const rules = riskQuery.data?.rules ?? [];
  const findings = categories.reduce((sum, c) => sum + Number(c.findings), 0);
  const denied = deniedQuery.data?.challenges ?? [];
  const deniedTotal = deniedQuery.data?.total ?? 0;
  const unresolvedTotal = unresolvedQuery.data?.total;
  const shadowServers = shadowQuery.data?.servers ?? [];

  return (
    <IdentitySection
      title="Security"
      meta={sectionMeta(
        canReadRisk
          ? [
              { count: findings, singular: "finding" },
              { count: deniedTotal, singular: "denied", plural: "denied" },
              { count: shadowServers.length, singular: "shadow server" },
            ]
          : [{ count: deniedTotal, singular: "denied challenge" }],
      )}
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <IdentityPanel
          title="Risk findings"
          // Findings is admin-only.
          handoffLabel={canReadRisk ? "Findings" : "Risk Events"}
          handoffHref={canReadRisk ? findingsHref({}) : handoffs.riskEvents}
          loading={riskQuery.isLoading}
          loadingVariant="block"
          error={riskQuery.isError && categories.length === 0}
          refreshFailed={riskQuery.isError && categories.length > 0}
          onRetry={retryFailed(riskQuery)}
          footer={
            canReadRisk
              ? riskMatchedOnLabel(identity.externalUserIds)
              : undefined
          }
        >
          {!canReadRisk ? (
            <IdentityPanelEmpty>{RISK_UNAVAILABLE}</IdentityPanelEmpty>
          ) : categories.length === 0 ? (
            <IdentityPanelEmpty>No findings in this window.</IdentityPanelEmpty>
          ) : (
            <div className="px-4 py-4">
              <ShareBar
                segments={categories.map((category) => ({
                  key: category.category,
                  label: ruleCategoryLabel(category.category),
                  value: Number(category.findings),
                  valueLabel: Number(category.findings).toLocaleString(),
                }))}
                ariaLabel="Findings by category"
                onSelect={(category) => showFindings({ category })}
              />
            </div>
          )}
        </IdentityPanel>

        <IdentityPanel
          title="Findings by rule"
          handoffLabel="Risk Events"
          handoffHref={handoffs.riskEvents}
          loading={riskQuery.isLoading}
          loadingVariant="block"
          error={riskQuery.isError && rules.length === 0}
          refreshFailed={riskQuery.isError && rules.length > 0}
          onRetry={retryFailed(riskQuery)}
          footer={
            canReadRisk && rules.length > TOP_RULES
              ? `Top ${TOP_RULES} of ${rules.length} rules`
              : undefined
          }
        >
          {!canReadRisk ? (
            <IdentityPanelEmpty>{RISK_UNAVAILABLE}</IdentityPanelEmpty>
          ) : rules.length === 0 ? (
            <IdentityPanelEmpty>
              No rule matched this identity in this window.
            </IdentityPanelEmpty>
          ) : (
            <div className="px-4 py-4">
              <RankedBarList
                items={rules.slice(0, TOP_RULES).map((rule, index) => ({
                  key: rule.ruleId || `__none_${index}`,
                  label: rule.ruleId
                    ? getRuleTitleFallback(rule.ruleId)
                    : "(no rule_id)",
                  value: Number(rule.findings),
                  onSelect: rule.ruleId
                    ? () => showFindings({ ruleId: rule.ruleId })
                    : undefined,
                }))}
              />
            </div>
          )}
        </IdentityPanel>

        <IdentityPanel
          title="Shadow MCP servers"
          handoffLabel="Shadow MCP"
          handoffHref={handoffs.shadowMcp}
          loading={shadowQuery.isLoading}
          error={shadowQuery.isError && shadowServers.length === 0}
          refreshFailed={shadowQuery.isError && shadowServers.length > 0}
          onRetry={retryFailed(shadowQuery)}
          footer={
            canReadRisk
              ? "Servers this person reached in this window, not the project-wide inventory."
              : undefined
          }
        >
          {!canReadRisk ? (
            <IdentityPanelEmpty>{RISK_UNAVAILABLE}</IdentityPanelEmpty>
          ) : shadowServers.length === 0 ? (
            <IdentityPanelEmpty>
              This identity reached no unsanctioned server.
            </IdentityPanelEmpty>
          ) : (
            shadowServers.map((server) => (
              <IdentityPanelRow
                key={server.serverSlug}
                // The canonical verdict, not the deprecated `access` string it
                // can disagree with.
                accent={
                  shadowMCPAccessSummaryOf(server).state === "blocked"
                    ? "destructive"
                    : undefined
                }
                title={server.serverName || server.urlHost}
                detail={
                  server.lastCalled ? (
                    <HumanizeDateTime date={server.lastCalled} />
                  ) : undefined
                }
                trailing={`${server.requestCount} calls`}
              />
            ))
          )}
        </IdentityPanel>

        <IdentityPanel
          title="Denied authorization challenges"
          handoffLabel="Roles & Permissions"
          handoffHref={handoffs.challenges}
          loading={deniedQuery.isLoading}
          error={deniedQuery.isError && denied.length === 0}
          refreshFailed={deniedQuery.isError && denied.length > 0}
          onRetry={retryFailed(deniedQuery)}
          footer={
            deniedTotal > 0 && unresolvedTotal !== undefined
              ? `${unresolvedTotal.toLocaleString()} of ${deniedTotal.toLocaleString()} still unresolved`
              : undefined
          }
        >
          {denied.length === 0 ? (
            <IdentityPanelEmpty>
              No denied checks for this identity.
            </IdentityPanelEmpty>
          ) : (
            denied
              .slice(0, DENIED_ROWS)
              .map((challenge) => (
                <IdentityPanelRow
                  key={challenge.id}
                  accent={challenge.resolvedAt ? undefined : "destructive"}
                  title={challenge.scope}
                  detail={<HumanizeDateTime date={challenge.timestamp} />}
                  trailing={challenge.resolvedAt ? "Resolved" : "Denied"}
                />
              ))
          )}
        </IdentityPanel>

        {/* Which AI tools this person runs is a security question about them,
            not a list of things they have connected — it sits here rather than
            on Connections for the same reason the denied challenges do. */}
        {isEmployee && (
          // Full width: the detections table is eight columns and 820px at
          // minimum, so half the grid would scroll it sideways on a laptop.
          <div className="md:col-span-2">
            {grantsLoading ? (
              // Grants still resolving: saying the permission is missing
              // would be false for most people who reach this page.
              <IdentityPanel title="Shadow AI" loading loadingVariant="block">
                {null}
              </IdentityPanel>
            ) : canReadDetections ? (
              <EmployeeShadowAISection userEmail={employeeEmail} />
            ) : (
              <IdentityPanel title="Shadow AI">
                <IdentityPanelEmpty>
                  Device-agent detections need the project:read permission.
                </IdentityPanelEmpty>
              </IdentityPanel>
            )}
          </div>
        )}
      </div>
    </IdentitySection>
  );
}
